import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// company_email_settings and its RPCs land in 20260910120000_email_multitenant_foundation.sql.
// Regenerate src/integrations/supabase/types.ts after applying it and drop these casts.
// deno-lint-ignore no-explicit-any
const db = supabase as any;

export type CustomDomainStatus = 'none' | 'pending' | 'verified' | 'failed';

export interface DnsRecord {
  type: 'CNAME' | 'TXT' | 'MX';
  host: string;
  value: string;
  purpose: 'dkim' | 'return_path' | 'spf' | 'other';
  verified: boolean;
}

export interface CompanyEmailSettings {
  company_id: string;
  company_name: string;
  public_key: string;
  /** Slug of the trading name used as the shared-domain sender; public_key is the fallback. */
  platform_local_part: string | null;
  from_name: string | null;
  from_local_part: string;
  custom_domain: string | null;
  custom_domain_status: CustomDomainStatus;
  dns_records: DnsRecord[] | null;
  domain_verified_at: string | null;
  domain_last_checked_at: string | null;
  forward_inbound_to: string | null;
  monthly_quota: number;
  sending_paused: boolean;
  paused_reason: string | null;
  paused_at: string | null;
  provider: string;
  sent_this_month: number;
}

export const EMAIL_SETTINGS_KEY = ['company-email-settings'] as const;

export function useEmailSettings() {
  return useQuery<CompanyEmailSettings | null>({
    queryKey: EMAIL_SETTINGS_KEY,
    queryFn: async () => {
      const { data, error } = await db.rpc('get_company_email_settings');
      if (error) throw error;
      const row = (data ?? [])[0];
      return row ? (row as CompanyEmailSettings) : null;
    },
    staleTime: 30_000,
  });
}

export function useUpdateEmailSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { from_name: string; from_local_part: string; forward_inbound_to: string }) => {
      const { error } = await db.rpc('update_company_email_settings', {
        p_from_name: input.from_name,
        p_from_local_part: input.from_local_part,
        p_forward_inbound_to: input.forward_inbound_to,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_SETTINGS_KEY }),
  });
}

export type EmailDomainAction = 'create' | 'verify' | 'remove' | 'test';

interface EmailDomainResponse {
  ok: boolean;
  status?: CustomDomainStatus;
  records?: DnsRecord[];
  from?: string;
  to?: string;
  error?: string;
  code?: string;
}

export function useEmailDomainAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: EmailDomainAction; domain?: string }): Promise<EmailDomainResponse> => {
      const { data, error } = await supabase.functions.invoke('email-domain', { body: input });
      if (error) throw new Error(error.message ?? 'Request failed');
      const res = data as EmailDomainResponse;
      if (!res?.ok) throw new Error(res?.error ?? 'Request failed');
      return res;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_SETTINGS_KEY }),
  });
}

// ── Super admin ─────────────────────────────────────────────────────────────

export interface EmailSendingOverviewRow {
  company_id: string;
  company_name: string;
  public_key: string;
  sent_this_month: number;
  monthly_quota: number;
  sending_paused: boolean;
  paused_reason: string | null;
  paused_at: string | null;
  custom_domain: string | null;
  custom_domain_status: CustomDomainStatus;
  sent_7d: number;
  bounced_7d: number;
  complained_7d: number;
}

export const EMAIL_OVERVIEW_KEY = ['email-sending-overview'] as const;

export function useEmailSendingOverview(enabled: boolean) {
  return useQuery<EmailSendingOverviewRow[]>({
    queryKey: EMAIL_OVERVIEW_KEY,
    enabled,
    queryFn: async () => {
      const { data, error } = await db.rpc('get_email_sending_overview');
      if (error) throw error;
      return (data ?? []) as EmailSendingOverviewRow[];
    },
  });
}

export function useSetEmailSendingPaused() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { companyId: string; paused: boolean; reason?: string }) => {
      const { error } = await db.rpc('set_email_sending_paused', {
        p_company: input.companyId,
        p_paused: input.paused,
        p_reason: input.reason ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_OVERVIEW_KEY }),
  });
}

export function useSetEmailMonthlyQuota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { companyId: string; quota: number; reason?: string }) => {
      const { error } = await db.rpc('set_email_monthly_quota', {
        p_company: input.companyId,
        p_quota: input.quota,
        p_reason: input.reason ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EMAIL_OVERVIEW_KEY }),
  });
}
